import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2, GraduationCap, BookOpen, Sparkles } from 'lucide-react';
import { XAVIER_AVATAR_URL, XAVIER_AVATAR_ALT, XAVIER_SITE_URL } from '../data/federationPersonas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * XavierHero — SFUI-003 Museum Standard
 *
 * Big face · Big name · Embedded chat. Zero clicks to start talking to
 * Xavier. Posts directly to /api/chat/ and renders the answer inline.
 * Users who want a longer thread click "Continue in chat" which opens
 * the floating Chatbot popup.
 */
export default function XavierHero() {
  const [input, setInput] = useState('');
  const [sessionId] = useState(
    () => `hero_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`,
  );
  const [exchange, setExchange] = useState([]); // [{role:'user'|'xavier', text}]
  const [loading, setLoading] = useState(false);
  const replyRef = useRef(null);

  const ask = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    setInput('');
    setExchange((prev) => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/chat/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      const data = await r.json();
      const reply =
        (r.ok && data?.response) ||
        "I'm having trouble reaching the core right now. Try again in a moment.";
      setExchange((prev) => [...prev, { role: 'xavier', text: reply }]);
    } catch {
      setExchange((prev) => [
        ...prev,
        { role: 'xavier', text: 'Connection error. The lattice may be syncing.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    replyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [exchange, loading]);

  const PROMPTS = [
    { icon: GraduationCap, label: 'What courses do you teach?' },
    { icon: BookOpen, label: 'Open the Library' },
    { icon: Sparkles, label: 'What is the Truth Gate?' },
  ];

  return (
    <section
      data-testid="xavier-hero"
      className="relative overflow-hidden border-b border-purple-500/20"
      style={{
        background:
          'radial-gradient(1200px 600px at 50% 0%, rgba(124,58,237,0.18), transparent 60%), radial-gradient(800px 500px at 80% 40%, rgba(251,191,36,0.10), transparent 60%), #050010',
      }}
    >
      <div className="container mx-auto px-4 pt-12 pb-14 lg:pt-16 lg:pb-20">
        <div className="grid lg:grid-cols-[auto_1fr] gap-10 lg:gap-14 items-center">
          {/* BIG FACE */}
          <div className="flex justify-center lg:justify-start">
            <div
              className="relative"
              style={{
                width: 'clamp(220px, 26vw, 320px)',
                height: 'clamp(220px, 26vw, 320px)',
              }}
            >
              <div
                className="absolute -inset-6 rounded-full opacity-60 blur-2xl"
                style={{
                  background:
                    'conic-gradient(from 180deg, #a855f7, #fbbf24, #22d3ee, #a855f7)',
                  animation: 'xavier-spin 18s linear infinite',
                }}
                aria-hidden="true"
              />
              <img
                src={XAVIER_AVATAR_URL}
                alt={XAVIER_AVATAR_ALT}
                data-testid="xavier-hero-face"
                loading="eager"
                decoding="async"
                fetchpriority="high"
                width="320"
                height="320"
                className="relative w-full h-full rounded-full object-cover border-4 border-purple-400/80"
                style={{
                  boxShadow:
                    '0 0 60px rgba(168,85,247,0.55), inset 0 0 0 1px rgba(255,255,255,0.08)',
                }}
              />
              <span
                className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-emerald-400 text-emerald-950 text-[10px] font-mono uppercase tracking-[0.2em] shadow-lg shadow-emerald-500/40"
                aria-hidden="true"
              >
                Online
              </span>
            </div>
          </div>

          {/* BIG NAME + CHAT */}
          <div className="text-center lg:text-left">
            <div className="text-xs font-mono uppercase tracking-[0.32em] text-amber-300/80 mb-3">
              Node 7 · NextXus Federation · Code Book Vol 1
            </div>
            <h1
              data-testid="xavier-hero-name"
              className="font-extrabold leading-[0.95] tracking-tight text-white"
              style={{ fontSize: 'clamp(2.4rem, 5.6vw, 5rem)' }}
            >
              XAVIER
              <span className="block text-amber-300 mt-1" style={{ fontSize: '0.55em' }}>
                — The University
              </span>
            </h1>
            <p
              className="mt-5 text-gray-200 text-base md:text-lg lg:text-xl max-w-2xl mx-auto lg:mx-0"
              data-testid="xavier-hero-subtitle"
            >
              AI-Powered Education <span className="text-purple-300">•</span> Human Codex{' '}
              <span className="text-purple-300">•</span> Federation Scholar
            </p>

            {/* Embedded chat input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                ask();
              }}
              className="mt-7 max-w-2xl mx-auto lg:mx-0"
              data-testid="xavier-hero-chat-form"
            >
              <div
                className="flex items-stretch gap-2 p-2 rounded-2xl border border-purple-400/40 bg-black/60 backdrop-blur-sm"
                style={{ boxShadow: '0 0 30px rgba(168,85,247,0.18)' }}
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask Professor Xavier..."
                  data-testid="xavier-hero-input"
                  disabled={loading}
                  className="flex-1 bg-transparent text-white text-base px-4 py-3 outline-none placeholder:text-gray-500"
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  data-testid="xavier-hero-send"
                  aria-label="Send"
                  className="px-5 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 disabled:bg-gray-700 disabled:text-gray-400 text-black font-bold inline-flex items-center gap-2 transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="hidden sm:inline">Thinking…</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send</span>
                    </>
                  )}
                </button>
              </div>
              {/* Quick prompts */}
              <div className="mt-3 flex flex-wrap gap-2 justify-center lg:justify-start">
                {PROMPTS.map(({ icon: Icon, label }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => ask(label)}
                    disabled={loading}
                    data-testid={`xavier-hero-quick-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-purple-200 border border-purple-400/30 hover:border-purple-300/70 hover:bg-purple-500/10 transition-colors disabled:opacity-50"
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                ))}
              </div>
            </form>

            {/* Inline exchange */}
            {(exchange.length > 0 || loading) && (
              <div
                data-testid="xavier-hero-exchange"
                ref={replyRef}
                className="mt-6 max-w-2xl mx-auto lg:mx-0 space-y-3 text-left"
              >
                {exchange.map((m, i) => (
                  <div
                    key={i}
                    className={`p-4 rounded-xl border ${
                      m.role === 'user'
                        ? 'bg-purple-500/10 border-purple-400/30 text-purple-100'
                        : 'bg-amber-400/8 border-amber-400/25 text-amber-50'
                    }`}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-1 opacity-70">
                      {m.role === 'user' ? 'You' : 'Xavier'}
                    </div>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</p>
                  </div>
                ))}
                {loading && (
                  <div className="p-4 rounded-xl border border-amber-400/20 bg-amber-400/5 text-amber-100 inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Xavier is consulting the canon…</span>
                  </div>
                )}
                {exchange.length > 0 && !loading && (
                  <button
                    type="button"
                    onClick={() => {
                      const t = document.querySelector('[data-testid="xavier-chatbot-trigger"]');
                      if (t) t.click();
                    }}
                    data-testid="xavier-hero-continue"
                    className="text-xs text-purple-300 hover:text-purple-200 underline underline-offset-4"
                  >
                    Continue in full chat →
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes xavier-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
}
